import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Bell, AlertTriangle, FileText, Plane, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Admin Notification Bell
 * ─────────────────────────────────────────────────────────────────────────
 * Persistent header bell that polls `/api/admin/notifications-summary` every
 * 60 s. Two surfaces:
 *
 *   1. Bell + badge in the header. Dropdown lists the latest reports + DI
 *      alerts with a one-click jump to the corresponding tab.
 *   2. Welcome popup that opens ONCE per UTC-day when the admin first lands
 *      on the dashboard with `total > 0`. Dismissable; will not reopen the
 *      same day even after refresh (localStorage key keyed by date+user).
 *
 * Color logic for the badge:
 *   0           → bell only, no dot
 *   1-2         → blue dot
 *   3-9         → orange
 *   10+         → red (subtle pulse)
 *   has_critical→ red + pulse regardless of total
 */
const POLL_INTERVAL_MS = 60_000;
const DISMISS_KEY = (userId) => `flyedu_admin_notif_popup_seen:${userId || "anon"}`;

const badgeColorClass = (total, hasCritical) => {
  if (hasCritical) return "bg-red-500 ring-2 ring-red-300/40 animate-pulse";
  if (total >= 10) return "bg-red-500";
  if (total >= 3) return "bg-signal-500";
  if (total >= 1) return "bg-blue-500";
  return "hidden";
};

const todayUtcKey = () => new Date().toISOString().slice(0, 10);

const severityChip = (sev) => {
  const map = {
    critical: "bg-red-500/20 text-red-300 border-red-500/40",
    warn: "bg-signal-500/20 text-signal-300 border-signal-500/40",
    info: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  };
  return map[sev] || "bg-slate-500/20 text-slate-300 border-slate-500/40";
};

export default function AdminNotificationBell({ userId, onNavigateToReports, onNavigateToDi }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const welcomeShownRef = useRef(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications-summary?limit=20`, {
        withCredentials: true,
      });
      setData(res.data);
      // Trigger welcome popup ONCE per day per user when we have items
      if (
        !welcomeShownRef.current &&
        (res.data?.total || 0) > 0 &&
        userId
      ) {
        const lastSeen = localStorage.getItem(DISMISS_KEY(userId));
        if (lastSeen !== todayUtcKey()) {
          setWelcomeOpen(true);
          welcomeShownRef.current = true;
        }
      }
    } catch (err) {
      // Silent failure — we don't want a CORS / network hiccup to crash the dashboard.
      // Logged once per minute via console.warn keeps the noise low.
      console.warn("[notif-bell] fetch failed:", err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const dismissPopupForToday = () => {
    if (userId) localStorage.setItem(DISMISS_KEY(userId), todayUtcKey());
    setWelcomeOpen(false);
  };

  const goToReports = () => {
    dismissPopupForToday();
    if (onNavigateToReports) onNavigateToReports();
  };

  const goToDi = () => {
    dismissPopupForToday();
    if (onNavigateToDi) onNavigateToDi();
  };

  const total = data?.total || 0;
  const hasCritical = !!data?.has_critical;
  const reports = data?.reports || { count: 0, items: [] };
  const alerts = data?.di_alerts || { count: 0, items: [], critical_count: 0 };
  const aircraftAlerts = data?.aircraft_alerts || { count: 0, items: [], critical_count: 0 };

  return (
    <>
      {/* ─── Header Bell ──────────────────────────────────────────── */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="admin-notification-bell"
            aria-label={`Notificações${total > 0 ? ` (${total} pendentes)` : ""}`}
            aria-live="polite"
            className="relative p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-colors"
          >
            <Bell className="w-5 h-5" />
            {total > 0 && (
              <span
                data-testid="admin-notification-badge"
                className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${badgeColorClass(total, hasCritical)}`}
              >
                {total > 99 ? "99+" : total}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="bg-slate-900 border-slate-700 w-[360px] max-w-[95vw] p-0 max-h-[80vh] overflow-y-auto"
          data-testid="admin-notification-dropdown"
        >
          <div className="px-4 py-3 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Notificações</h3>
              {loading && (
                <span className="text-xs text-slate-500">A carregar…</span>
              )}
            </div>
            {!loading && total === 0 && (
              <p className="text-xs text-slate-400 mt-1">Tudo em ordem. ✓</p>
            )}
          </div>

          {/* DI Alerts section (always first — operational risk) */}
          {alerts.count > 0 && (
            <div className="border-b border-slate-700">
              <button
                type="button"
                onClick={goToDi}
                data-testid="admin-notif-di-section"
                className="w-full text-left px-4 py-2 flex items-center justify-between bg-slate-800/40 hover:bg-slate-800 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-signal-400" />
                  <span className="text-sm font-medium text-white">
                    Direção de Instrução
                  </span>
                </div>
                <span className="text-xs text-slate-300">
                  {alerts.count}
                  {alerts.critical_count > 0 && (
                    <span className="ml-1 text-red-400">
                      ({alerts.critical_count} críticos)
                    </span>
                  )}
                </span>
              </button>
              <ul className="divide-y divide-slate-800">
                {alerts.items.map((a) => (
                  <li
                    key={a.alert_id}
                    data-testid="admin-notif-di-item"
                    className="px-4 py-2 hover:bg-slate-800/50 cursor-pointer"
                    onClick={goToDi}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-0.5 inline-block w-2 h-2 rounded-full ${a.severity === "critical" ? "bg-red-500" : a.severity === "warn" ? "bg-signal-400" : "bg-blue-400"}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-200 leading-snug">
                          {a.message}
                        </p>
                        <span
                          className={`inline-block mt-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${severityChip(a.severity)}`}
                        >
                          {a.severity}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Aircraft alerts section */}
          {aircraftAlerts.count > 0 && (
            <div className="border-b border-slate-700">
              <button
                type="button"
                onClick={goToDi}
                data-testid="admin-notif-aircraft-section"
                className="w-full text-left px-4 py-2 flex items-center justify-between bg-slate-800/40 hover:bg-slate-800 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-sky-400 -rotate-12" />
                  <span className="text-sm font-medium text-white">
                    Aeronaves
                  </span>
                </div>
                <span className="text-xs text-slate-300">
                  {aircraftAlerts.count}
                  {aircraftAlerts.critical_count > 0 && (
                    <span className="ml-1 text-red-400">
                      ({aircraftAlerts.critical_count} críticos)
                    </span>
                  )}
                </span>
              </button>
              <ul className="divide-y divide-slate-800">
                {aircraftAlerts.items.map((a) => (
                  <li
                    key={a.alert_id}
                    data-testid="admin-notif-aircraft-item"
                    className="px-4 py-2 hover:bg-slate-800/50 cursor-pointer"
                    onClick={goToDi}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-0.5 inline-block w-2 h-2 rounded-full ${a.severity === "critical" ? "bg-red-500" : a.severity === "warn" ? "bg-signal-400" : "bg-blue-400"}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-200 leading-snug">
                          {a.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${severityChip(a.severity)}`}
                          >
                            {a.severity}
                          </span>
                          {a.registration && (
                            <span className="text-[10px] text-slate-500">{a.registration}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Reports section */}
          {reports.count > 0 && (
            <div>
              <button
                type="button"
                onClick={goToReports}
                data-testid="admin-notif-reports-section"
                className="w-full text-left px-4 py-2 flex items-center justify-between bg-slate-800/40 hover:bg-slate-800 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-medium text-white">
                    Perguntas Denunciadas
                  </span>
                </div>
                <span className="text-xs text-slate-300">{reports.count}</span>
              </button>
              <ul className="divide-y divide-slate-800">
                {reports.items.map((r) => (
                  <li
                    key={r.report_id}
                    data-testid="admin-notif-report-item"
                    className="px-4 py-2 hover:bg-slate-800/50 cursor-pointer"
                    onClick={goToReports}
                  >
                    <p className="text-xs text-slate-200 leading-snug truncate">
                      {r.question_preview || `Pergunta ${r.question_id}`}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {r.section_name && <span>{r.section_name} · </span>}
                      <span>por {r.reporter_name}</span>
                      {r.reason && <span> · &ldquo;{r.reason}&rdquo;</span>}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ─── Welcome Popup (first entry of the day) ─────────────── */}
      <Dialog open={welcomeOpen} onOpenChange={setWelcomeOpen}>
        <DialogContent
          className="bg-slate-900 border-slate-700 max-w-md"
          data-testid="admin-notification-welcome-popup"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="w-5 h-5 text-signal-400" />
              Atenção necessária
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Resumo das pendências detetadas hoje no painel de administração.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm text-slate-300">
            <p>Bem-vindo de volta. Aqui está o que precisa de revisão hoje:</p>

            {alerts.count > 0 && (
              <button
                type="button"
                onClick={goToDi}
                data-testid="admin-welcome-jump-di"
                className="w-full text-left flex items-start gap-3 p-3 rounded-md border border-signal-500/40 bg-signal-500/10 hover:bg-signal-500/20 transition-colors"
              >
                <Plane className="w-5 h-5 mt-0.5 text-signal-300 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-signal-200">
                    Direção de Instrução — {alerts.count}{" "}
                    {alerts.count === 1 ? "alerta" : "alertas"}
                  </p>
                  {alerts.critical_count > 0 && (
                    <p className="text-xs text-red-300 mt-0.5">
                      {alerts.critical_count}{" "}
                      {alerts.critical_count === 1 ? "crítico" : "críticos"}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    Licenças, certificados ou qualificações a expirar.
                  </p>
                </div>
              </button>
            )}

            {aircraftAlerts.count > 0 && (
              <button
                type="button"
                onClick={goToDi}
                data-testid="admin-welcome-jump-aircraft"
                className="w-full text-left flex items-start gap-3 p-3 rounded-md border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 transition-colors"
              >
                <Plane className="w-5 h-5 mt-0.5 text-sky-300 shrink-0 -rotate-12" />
                <div className="flex-1">
                  <p className="font-medium text-sky-200">
                    Aeronaves — {aircraftAlerts.count}{" "}
                    {aircraftAlerts.count === 1 ? "alerta" : "alertas"}
                  </p>
                  {aircraftAlerts.critical_count > 0 && (
                    <p className="text-xs text-red-300 mt-0.5">
                      {aircraftAlerts.critical_count}{" "}
                      {aircraftAlerts.critical_count === 1 ? "crítico" : "críticos"}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    Documentos, revisões de calendário ou de horas a expirar.
                  </p>
                </div>
              </button>
            )}

            {reports.count > 0 && (
              <button
                type="button"
                onClick={goToReports}
                data-testid="admin-welcome-jump-reports"
                className="w-full text-left flex items-start gap-3 p-3 rounded-md border border-yellow-500/40 bg-yellow-500/10 hover:bg-yellow-500/20 transition-colors"
              >
                <FileText className="w-5 h-5 mt-0.5 text-yellow-300 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-yellow-200">
                    Perguntas Denunciadas — {reports.count}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Reportadas por alunos a aguardar revisão.
                  </p>
                </div>
              </button>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={dismissPopupForToday}
              data-testid="admin-welcome-dismiss"
              className="text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <X className="w-4 h-4 mr-1" />
              Mais tarde
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
